import { QueryClientProvider } from "@tanstack/react-query";
import { lazy, Suspense } from "react";
import { useTranslation } from "react-i18next";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";

import {
  ChunkErrorBoundary,
  setupVitePreloadErrorHandler,
} from "@/components/common/ChunkErrorHandler";
import { ScrollToTop } from "@/components/common/ScrollToTop";
import { AppLayout } from "@/components/layout/AppLayout";
import { AppCustomerLayout } from "@/components/layout/customer/AppCustomerLayout";
import { Toaster } from "@/components/ui/sonner";
import { useBrandingEffect } from "@/hooks/useBrandingEffect";
import { queryClient } from "@/lib/queryClient";
import { ContentTypeEnum } from "@/types/content.types";

import { ProtectedManagerRoute } from "./components/auth/ProtectedManagerRoute";
import { ProtectedStaffRoute } from "./components/auth/ProtectedStaffRoute";
import { SettingPage } from "./pages/customer/SettingPage";

setupVitePreloadErrorHandler();

// Lazy loading components
const LoginPage = lazy(() =>
  import("@/pages/LoginPage").then((m) => ({ default: m.LoginPage })),
);
const StaffPosPage = lazy(() =>
  import("@/pages/StaffPosPage").then((m) => ({
    default: m.StaffPosPage,
  })),
);
const VoucherListTestPage = lazy(() =>
  import("@/pages/VoucherListTestPage").then((m) => ({
    default: m.VoucherListTestPage,
  })),
);
const TransactionHistoryPage = lazy(() =>
  import("@/pages/TransactionHistoryPage").then((m) => ({
    default: m.TransactionHistoryPage,
  })),
);
const DashboardPage = lazy(() =>
  import("@/pages/DashboardPage").then((m) => ({ default: m.DashboardPage })),
);

// Customer page lazy imports
const HomePage = lazy(() =>
  import("@/pages/customer/HomePage").then((m) => ({ default: m.HomePage })),
);
const RestaurantPage = lazy(() =>
  import("@/pages/customer/RestaurantPage").then((m) => ({
    default: m.RestaurantPage,
  })),
);
const LoyaltyPage = lazy(() =>
  import("@/pages/customer/LoyaltyPage").then((m) => ({
    default: m.LoyaltyPage,
  })),
);
const VouchersPage = lazy(() =>
  import("@/pages/customer/VouchersPage").then((m) => ({
    default: m.VouchersPage,
  })),
);
const VoucherDetailPage = lazy(() =>
  import("@/pages/customer/VoucherDetailPage").then((m) => ({
    default: m.VoucherDetailPage,
  })),
);
const MyVoucherDetailPage = lazy(() =>
  import("@/pages/customer/MyVoucherDetailPage").then((m) => ({
    default: m.MyVoucherDetailPage,
  })),
);
const ArticleDetailPage = lazy(() =>
  import("@/pages/customer/ArticleDetailPage").then((m) => ({
    default: m.ArticleDetailPage,
  })),
);
const ProfilePage = lazy(() =>
  import("@/pages/customer/ProfilePage").then((m) => ({
    default: m.ProfilePage,
  })),
);
const CustomerTransactionHistoryPage = lazy(() =>
  import("@/pages/customer/TransactionHistoryPage").then((m) => ({
    default: m.CustomerTransactionHistoryPage,
  })),
);
const EditProfilePage = lazy(() =>
  import("@/pages/customer/profile/EditProfilePage").then((m) => ({
    default: m.EditProfilePage,
  })),
);
const ChangePasswordPage = lazy(() =>
  import("@/pages/customer/profile/ChangePasswordPage").then((m) => ({
    default: m.ChangePasswordPage,
  })),
);
const MembershipPage = lazy(() =>
  import("@/pages/customer/MembershipPage").then((m) => ({
    default: m.MembershipPage,
  })),
);
const NotificationPage = lazy(() =>
  import("@/pages/customer/NotificationPage").then((m) => ({
    default: m.NotificationPage,
  })),
);

// Customer Auth pages lazy imports
const CustomerLoginPage = lazy(() =>
  import("@/pages/customer/auth/LoginPage").then((m) => ({
    default: m.CustomerLoginPage,
  })),
);
const CustomerSignUpPage = lazy(() =>
  import("@/pages/customer/auth/SignUpPage").then((m) => ({
    default: m.CustomerSignUpPage,
  })),
);
const CustomerVerifyAccountPage = lazy(() =>
  import("@/pages/customer/auth/VerifyAccountPage").then((m) => ({
    default: m.CustomerVerifyAccountPage,
  })),
);
const CustomerForgotPasswordPage = lazy(() =>
  import("@/pages/customer/auth/ForgotPasswordPage").then((m) => ({
    default: m.CustomerForgotPasswordPage,
  })),
);
const CustomerForgotPasswordVerifyPage = lazy(() =>
  import("@/pages/customer/auth/ForgotPasswordVerifyPage").then((m) => ({
    default: m.CustomerForgotPasswordVerifyPage,
  })),
);
const CustomerResetPasswordPage = lazy(() =>
  import("@/pages/customer/auth/ResetPasswordPage").then((m) => ({
    default: m.CustomerResetPasswordPage,
  })),
);
const OAuthCallbackPage = lazy(() =>
  import("@/pages/customer/auth/OAuthCallbackPage").then((m) => ({
    default: m.OAuthCallbackPage,
  })),
);
const InviteHandler = lazy(() =>
  import("@/pages/customer/InviteHandler").then((m) => ({
    default: m.InviteHandler,
  })),
);
const ReferralPage = lazy(() =>
  import("@/pages/customer/profile/ReferralPage").then((m) => ({
    default: m.ReferralPage,
  })),
);
const ContactUsPage = lazy(() =>
  import("@/pages/customer/profile/ContactUsPage").then((m) => ({
    default: m.ContactUsPage,
  })),
);
const ContentPage = lazy(() =>
  import("@/pages/customer/profile/ContentPage").then((m) => ({
    default: m.ContentPage,
  })),
);
const FAQPage = lazy(() =>
  import("@/pages/customer/profile/FAQPage").then((m) => ({
    default: m.FAQPage,
  })),
);

// Loading component for Suspense
const PageLoader = () => (
  <div className="flex h-[50vh] w-full items-center justify-center">
    <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
  </div>
);

// Domain detection helper
const isStaffDomain = () => {
  const hostname = window.location.hostname;
  return hostname.startsWith("staff.");
};

// Staff Application Routes
const StaffRoutes = () => (
  <Routes>
    <Route element={<AppLayout />}>
      {/* Public routes */}
      <Route path="/login" element={<LoginPage />} />

      {/* Protected staff routes */}
      <Route element={<ProtectedStaffRoute />}>
        <Route path="/pos" element={<StaffPosPage />} />
        <Route path="/list-voucher-test" element={<VoucherListTestPage />} />

        {/* Manager-only routes */}
        <Route element={<ProtectedManagerRoute />}>
          <Route path="/transactions" element={<TransactionHistoryPage />} />
          <Route path="/dashboard" element={<DashboardPage />} />
        </Route>
      </Route>

      {/* Default redirect for staff */}
      <Route path="*" element={<Navigate to="/pos" replace />} />
    </Route>
  </Routes>
);

// Map of route paths to their corresponding page components
const customerPageComponents: Record<string, React.ComponentType> = {
  "/home": HomePage,
  "/restaurant": RestaurantPage,
  "/loyalty": LoyaltyPage,
  "/vouchers": VouchersPage,
  "/profile": ProfilePage,
  "/profile/transactions": CustomerTransactionHistoryPage,
  "/profile/edit": EditProfilePage,
  "/profile/change-password": ChangePasswordPage,
  "/profile/membership": MembershipPage,
  "/profile/referral": ReferralPage,
  "/contact-us": ContactUsPage,
  "/notifications": NotificationPage,
};

// Customer Application Routes
const CustomerRoutes = () => {
  const { t } = useTranslation();
  return (
    <Routes>
      {/* Auth routes - without layout */}
      <Route path="/login" element={<CustomerLoginPage />} />
      <Route path="/signup" element={<CustomerSignUpPage />} />
      <Route path="/verify-account" element={<CustomerVerifyAccountPage />} />
      <Route path="/forgot-password" element={<CustomerForgotPasswordPage />} />
      <Route
        path="/forgot-password-verify"
        element={<CustomerForgotPasswordVerifyPage />}
      />
      <Route path="/reset-password" element={<CustomerResetPasswordPage />} />
      <Route path="/auth/callback" element={<OAuthCallbackPage />} />
      <Route path="/signup/:code" element={<InviteHandler />} />

      {/* Main app routes - with layout */}
      <Route element={<AppCustomerLayout />}>
        {/* Protected customer routes - dynamically generated from navItems */}
        <Route>
          {Object.entries(customerPageComponents).map(([path, Component]) => (
            <Route key={path} path={path} element={<Component />} />
          ))}
          {/* Article detail route */}
          <Route path="/articles/:url" element={<ArticleDetailPage />} />
          {/* Voucher detail route */}
          <Route path="/vouchers/:id" element={<VoucherDetailPage />} />
          {/* My voucher detail route */}
          <Route path="/my-vouchers/:id" element={<MyVoucherDetailPage />} />
          {/* Content pages (Privacy Policy & Terms) */}
          <Route
            path="/terms"
            element={
              <ContentPage
                title={t("profile.menu.terms")}
                contentTitle="Terms & Condition"
                contentType={ContentTypeEnum.TermAndPolicy}
              />
            }
          />
          <Route path="/settings" element={<SettingPage />} />
          <Route
            path="/privacy-policy"
            element={
              <ContentPage
                title={t("profile.menu.privacy_policy")}
                contentTitle="Policy"
                contentType={ContentTypeEnum.Policy}
              />
            }
          />
          <Route path="/profile/faqs" element={<FAQPage />} />
        </Route>

        {/* Default redirect for customer */}
        <Route path="*" element={<Navigate to="/home" replace />} />
      </Route>
    </Routes>
  );
};

// Bootstrap component to handle domain switching
const AppBootstrap = () => {
  const isStaff = isStaffDomain();

  // Apply PWA branding (title, favicon, manifest) globally — including auth
  // pages that are rendered outside of AppCustomerLayout / AppLayout.
  useBrandingEffect();

  return (
    <ChunkErrorBoundary>
      <Suspense fallback={<PageLoader />}>
        {isStaff ? <StaffRoutes /> : <CustomerRoutes />}
      </Suspense>
    </ChunkErrorBoundary>
  );
};

const App = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter
        future={{
          v7_startTransition: true,
          v7_relativeSplatPath: true,
        }}
      >
        <ScrollToTop />
        <AppBootstrap />
        <Toaster position="top-right" />
      </BrowserRouter>
    </QueryClientProvider>
  );
};

export default App;
